import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './AddressForm.css';

/**
 * AddressForm Component
 * Form for creating or editing an address
 */
export default function AddressForm({
  initialData = null,
  onSubmit,
  onCancel,
  isLoading = false,
}) {
  const [formData, setFormData] = useState({
    addressType: 'HOME',
    fullName: '',
    mobile: '',
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false,
  });

  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        addressType: initialData.addressType || 'HOME',
        fullName: initialData.fullName || '',
        mobile: initialData.mobile || '',
        addressLine1: initialData.addressLine1 || '',
        addressLine2: initialData.addressLine2 || '',
        landmark: initialData.landmark || '',
        city: initialData.city || '',
        state: initialData.state || '',
        pincode: initialData.pincode || '',
        isDefault: initialData.isDefault || false,
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.length > 200) {
      newErrors.fullName = 'Full name must not exceed 200 characters';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = 'Enter valid 10-digit mobile (starting with 6-9)';
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address is required';
    } else if (formData.addressLine1.length > 500) {
      newErrors.addressLine1 = 'Address must not exceed 500 characters';
    }

    if (formData.addressLine2.length > 500) {
      newErrors.addressLine2 = 'Address line 2 must not exceed 500 characters';
    }

    if (formData.landmark.length > 200) {
      newErrors.landmark = 'Landmark must not exceed 200 characters';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    } else if (formData.city.length > 100) {
      newErrors.city = 'City must not exceed 100 characters';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    } else if (formData.state.length > 100) {
      newErrors.state = 'State must not exceed 100 characters';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^[1-9][0-9]{5}$/.test(formData.pincode)) {
      newErrors.pincode = 'Enter valid 6-digit pincode (not starting with 0)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
  ];

  return (
    <motion.form
      className="address-form"
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h3 className="form-title">
        {initialData ? 'Edit Address' : 'Add New Address'}
      </h3>

      {/* Address Type */}
      <div className="form-row">
        <div className="address-type-selector">
          <label className="type-label">Address Type</label>
          <div className="type-options">
            <button
              type="button"
              className={`type-btn ${formData.addressType === 'HOME' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, addressType: 'HOME' }))}
            >
              🏠 Home
            </button>
            <button
              type="button"
              className={`type-btn ${formData.addressType === 'WORK' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, addressType: 'WORK' }))}
            >
              💼 Work
            </button>
            <button
              type="button"
              className={`type-btn ${formData.addressType === 'OTHER' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, addressType: 'OTHER' }))}
            >
              📍 Other
            </button>
          </div>
        </div>
      </div>

      {/* Full Name & Mobile */}
      <div className="form-row two-col">
        <div className="form-group">
          <label htmlFor="fullName">Full Name *</label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter full name"
            maxLength={200}
            className={errors.fullName ? 'error' : ''}
          />
          {errors.fullName && <span className="error-msg">{errors.fullName}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="mobile">Mobile Number *</label>
          <input
            type="tel"
            id="mobile"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            placeholder="10-digit mobile number"
            maxLength="10"
            className={errors.mobile ? 'error' : ''}
          />
          {errors.mobile && <span className="error-msg">{errors.mobile}</span>}
        </div>
      </div>

      {/* Address Line 1 */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="addressLine1">Address Line 1 *</label>
          <input
            type="text"
            id="addressLine1"
            name="addressLine1"
            value={formData.addressLine1}
            onChange={handleChange}
            placeholder="House/Flat No., Building Name, Street"
            maxLength={500}
            className={errors.addressLine1 ? 'error' : ''}
          />
          {errors.addressLine1 && <span className="error-msg">{errors.addressLine1}</span>}
        </div>
      </div>

      {/* Address Line 2 */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="addressLine2">Address Line 2 (Optional)</label>
          <input
            type="text"
            id="addressLine2"
            name="addressLine2"
            value={formData.addressLine2}
            onChange={handleChange}
            placeholder="Area, Colony, Sector"
            maxLength={500}
            className={errors.addressLine2 ? 'error' : ''}
          />
          {errors.addressLine2 && <span className="error-msg">{errors.addressLine2}</span>}
        </div>
      </div>

      {/* Landmark */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="landmark">Landmark (Optional)</label>
          <input
            type="text"
            id="landmark"
            name="landmark"
            value={formData.landmark}
            onChange={handleChange}
            placeholder="Nearby landmark for easy delivery"
            maxLength={200}
            className={errors.landmark ? 'error' : ''}
          />
          {errors.landmark && <span className="error-msg">{errors.landmark}</span>}
        </div>
      </div>

      {/* City, State, Pincode */}
      <div className="form-row three-col">
        <div className="form-group">
          <label htmlFor="city">City *</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City"
            maxLength={100}
            className={errors.city ? 'error' : ''}
          />
          {errors.city && <span className="error-msg">{errors.city}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="state">State *</label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            className={errors.state ? 'error' : ''}
          >
            <option value="">Select State</option>
            {indianStates.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
          {errors.state && <span className="error-msg">{errors.state}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="pincode">Pincode *</label>
          <input
            type="text"
            id="pincode"
            name="pincode"
            value={formData.pincode}
            onChange={handleChange}
            placeholder="6-digit pincode"
            maxLength="6"
            className={errors.pincode ? 'error' : ''}
          />
          {errors.pincode && <span className="error-msg">{errors.pincode}</span>}
        </div>
      </div>

      {/* Set as Default */}
      <div className="form-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isDefault"
            checked={formData.isDefault}
            onChange={handleChange}
          />
          <span>Set as default address</span>
        </label>
      </div>

      {/* Actions */}
      <div className="form-actions">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-submit"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : (initialData ? 'Update Address' : 'Save Address')}
        </button>
      </div>
    </motion.form>
  );
}
